// SPDX-License-Identifier: AGPL-3.0-or-later

import '@app/app/fonts/radio_canada_big/radio-canada-big.css';
import Accessibility from '@app/features/accessibility/state/Accessibility';
import {getUserAvatarURL} from '@app/features/user/utils/AvatarUtils';
import * as NicknameUtils from '@app/features/user/utils/NicknameUtils';
import type {VoiceParticipantAvatarEntry} from '@app/features/voice/components/VoiceParticipantAvatarList';
import {clickRoomStageButton} from '@app/features/voice_scene/commands/RoomStageCommands';
import styles from '@app/features/voice_scene/components/VoiceSceneStageBoard.module.css';
import type {VoiceRoomStage} from '@fluxer/schema/src/domains/voice_seat/VoiceRoomStageSchemas';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useState} from 'react';

const SCOREBOARD_DESCRIPTOR = msg({
	message: 'Scores',
	comment: "3D voice room TV: accessible name of the scoreboard a bot shows on the room's TV board.",
});

type Board = Extract<NonNullable<VoiceRoomStage['tv']>, {type: 'board'}>;

const hex = (rgb: number): string => `#${rgb.toString(16).padStart(6, '0')}`;

// Dark text on a light accent, white on a dark one (relative luminance, sRGB).
function onAccent(rgb: number): string {
	const channel = (shift: number) => {
		const value = ((rgb >> shift) & 0xff) / 255;
		return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
	};
	const luminance = 0.2126 * channel(16) + 0.7152 * channel(8) + 0.0722 * channel(0);
	return luminance > 0.36 ? '#10121a' : '#ffffff';
}

// The board a bot shows on the room TV (or, in a room without one, as a sign over its host): a title, a text whose line
// breaks stay, a scoreboard and the buttons of the bot's own message. Laid out at the screen layer's width and sized by
// it (cqw), so it reads on a TV seen from across the room. Three looks: neon (a game show's dark glass), tahta (a
// chalkboard in a wooden frame) and sade (a clean light card); the bot's accent colours each. It comes in once (a new
// question comes in again, not under reduced motion) and never moves on its own after that. A scoreboard row about
// someone in the room shows their own name, never the bot's text for it.
export const VoiceSceneStageBoard = observer(function VoiceSceneStageBoard({
	board,
	bot,
	guildId,
	channelId,
	entries,
}: {
	board: Board;
	bot: VoiceRoomStage['bot'];
	guildId: string | null;
	channelId: string;
	entries: ReadonlyArray<VoiceParticipantAvatarEntry>;
}) {
	const {i18n} = useLingui();
	const [pending, setPending] = useState(false);
	const numbers = new Intl.NumberFormat(i18n.locale);
	const style =
		board.accent === null
			? undefined
			: ({'--stage-accent': hex(board.accent), '--stage-on-accent': onAccent(board.accent)} as React.CSSProperties);
	const buttons = board.buttons;
	const click = async (customId: string) => {
		if (!buttons || pending) return;
		setPending(true);
		try {
			await clickRoomStageButton(i18n, channelId, buttons.message_id, customId);
		} finally {
			setPending(false);
		}
	};
	return (
		<div
			className={styles.board}
			data-style={board.style}
			data-has-scores={board.scores.length > 0 ? '' : undefined}
			data-still={Accessibility.useReducedMotion ? '' : undefined}
			style={style}
			data-stage-board=""
			data-flx="voice-scene.voice-scene-stage-board.board"
		>
			<div
				key={`${board.title}\n${board.text}`}
				className={styles.surface}
				data-flx="voice-scene.voice-scene-stage-board.surface"
			>
				<header className={styles.header} data-flx="voice-scene.voice-scene-stage-board.header">
					<span className={styles.bot} data-flx="voice-scene.voice-scene-stage-board.bot">
						<img
							className={styles.botAvatar}
							src={getUserAvatarURL({id: bot.id, avatar: bot.avatar})}
							alt=""
							draggable={false}
							data-flx="voice-scene.voice-scene-stage-board.bot-avatar"
						/>
						<span className={styles.botName} data-flx="voice-scene.voice-scene-stage-board.bot-name">
							{bot.name}
						</span>
					</span>
					{board.title ? (
						<h2 className={styles.title} data-flx="voice-scene.voice-scene-stage-board.title">
							{board.title}
						</h2>
					) : null}
				</header>
				<div className={styles.body} data-flx="voice-scene.voice-scene-stage-board.body">
					{board.text ? (
						<p className={styles.text} data-stage-text="" data-flx="voice-scene.voice-scene-stage-board.text">
							{board.text}
						</p>
					) : null}
					{board.scores.length > 0 ? (
						<ol
							className={styles.scores}
							aria-label={i18n._(SCOREBOARD_DESCRIPTOR)}
							data-flx="voice-scene.voice-scene-stage-board.scores"
						>
							{board.scores.map((score, index) => {
								const user = score.user_id ? entries.find((entry) => entry.userId === score.user_id)?.user : undefined;
								return (
									<li
										key={index}
										className={styles.score}
										data-first={index === 0 ? '' : undefined}
										data-stage-score=""
										data-flx="voice-scene.voice-scene-stage-board.score"
									>
										<span
											className={styles.rank}
											data-stage-rank=""
											data-flx="voice-scene.voice-scene-stage-board.rank"
										>
											{index + 1}
										</span>
										{user ? (
											<img
												className={styles.scoreAvatar}
												src={getUserAvatarURL({id: user.id, avatar: user.avatar})}
												alt=""
												draggable={false}
												data-flx="voice-scene.voice-scene-stage-board.score-avatar"
											/>
										) : null}
										<span
											className={styles.scoreName}
											data-stage-score-name=""
											data-flx="voice-scene.voice-scene-stage-board.score-name"
										>
											{user ? NicknameUtils.getNickname(user, guildId, channelId) || score.name : score.name}
										</span>
										<span
											className={styles.scoreValue}
											data-stage-score-value=""
											data-flx="voice-scene.voice-scene-stage-board.score-value"
										>
											{numbers.format(score.value)}
										</span>
									</li>
								);
							})}
						</ol>
					) : null}
				</div>
				{buttons ? (
					<div className={styles.buttons} data-flx="voice-scene.voice-scene-stage-board.buttons">
						{buttons.items.map((item) => (
							<button
								key={item.custom_id}
								type="button"
								className={styles.button}
								data-button-style={item.style}
								disabled={item.disabled || pending}
								onClick={() => void click(item.custom_id)}
								data-flx="voice-scene.voice-scene-stage-board.button.click"
							>
								{item.label}
							</button>
						))}
					</div>
				) : null}
			</div>
		</div>
	);
});
